import {BatteryCharge, FormControl, FuelLevel, Label, Separator} from 'platform/components';
import {GridItem, HStack, Show, Text, VStack} from 'platform/foundation';
import {DateTimeFormat, useFormatNumber} from 'platform/locale';

import {useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';
import {isNegative, isNumber} from 'ramda-adjunct';

import {RentalOrderResponseBody} from '@dms/api/rental';
import i18n from '@dms/i18n';
import {BATTERY_CHARGE_MAX, BATTERY_CHARGE_MIN} from '@dms/shared';

import {EMPTY_PLACEHOLDER, Nullish, parseDate} from 'shared/web';

import {parseBatteryCharge} from '../../../utils/parseBatteryCharge';
import {MAX_FUEL_TANK_RATIO, parseFuelTankRatio} from '../../../utils/parseFuelTankRatio';
import {ReturnVehicleFormValues} from '../types/ReturnVehicleFormValues';

interface ConditionInformationRowProps {
  rentalOrder: RentalOrderResponseBody | Nullish;
  control: FormControl<ReturnVehicleFormValues>;
}

export function ConditionInformationRow(props: ConditionInformationRowProps) {
  const mileageWhenReturned = useWatch({control: props.control, name: 'actualMileage'});
  const formatNumber = useFormatNumber();

  const plannedReturnDate = props.rentalOrder?.plannedRentalPeriod?.to;
  const actualMileage = `${formatNumber(props.rentalOrder?.rentVehicle?.actualMileage) ?? EMPTY_PLACEHOLDER} ${i18n.t('general.metric.km')}`;

  const totalKmTraveled =
    (mileageWhenReturned ?? 0) - (props.rentalOrder?.rentVehicle?.actualMileage ?? 0);
  const formattedTotalKmTraveled = isNumber(totalKmTraveled)
    ? `${formatNumber(totalKmTraveled)} ${i18n.t('general.metric.km')}`
    : EMPTY_PLACEHOLDER;

  const fuelTankRatio = parseFuelTankRatio(props.rentalOrder?.rentVehicle?.fuelTank);
  const batteryCharge = parseBatteryCharge(props.rentalOrder?.rentVehicle?.batteryCharge);

  return (
    <>
      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.plannedReturnDate')}</Label>
        <Text alternative size="small">
          {isNotNil(plannedReturnDate) ? (
            <DateTimeFormat format="dateTimeShort" value={parseDate(plannedReturnDate)} />
          ) : (
            EMPTY_PLACEHOLDER
          )}
        </Text>
      </VStack>

      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.kmStatusWhenRented')}</Label>
        <Text alternative size="small">
          {actualMileage}
        </Text>
      </VStack>

      <GridItem span={[1, 2, 4, 2]}>
        <HStack spacing={4} align="flex-end">
          <FuelLevel
            label={i18n.t('page.rentalOrder.labels.fuelTankWhenRented')}
            value={fuelTankRatio}
            max={MAX_FUEL_TANK_RATIO}
            isDisabled
            data-testid="rentalOrderFuelTankWhenRented"
          />

          <Show when={isNotNil(batteryCharge)}>
            <BatteryCharge
              label={i18n.t('page.rentalOrder.labels.batteryChargeWhenRented')}
              value={batteryCharge}
              min={BATTERY_CHARGE_MIN}
              max={BATTERY_CHARGE_MAX}
              isDisabled
              data-testid="rentalOrderBatteryChargeWhenRented"
            />
          </Show>
        </HStack>
      </GridItem>

      <VStack spacing={1}>
        <Label>{i18n.t('page.rentalOrder.labels.totalKmTraveled')}</Label>
        <Text alternative size="small" color={isNegative(totalKmTraveled) ? 'danger' : undefined}>
          {formattedTotalKmTraveled}
        </Text>
      </VStack>

      <GridItem span={[1, 2, 4]}>
        <Separator spacing={0} />
      </GridItem>
    </>
  );
}
